import { useDropzone } from 'react-dropzone';
import { LoadingOverlay } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { useAuth0 } from '@auth0/auth0-react';
import { useState, useCallback } from 'react';
import { getPresignedURLs, notifyFileUpload } from '../../../services/removeFiles';
import { safeAxiosPut } from '../../../utils/safeAxios';
import { PresignedURL, GetPresignedURLsResponse } from '../../../interfaces/types';

interface FileDropZoneProps {
  onFilesProcessed: (summaries: string) => void;
}

export default function FileDropZone({ onFilesProcessed }: FileDropZoneProps) {
  const { t } = useTranslation();
  const { getAccessTokenSilently } = useAuth0();
  const [isLLMLoading, setIsLLMLoading] = useState<boolean>(false);

  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      setIsLLMLoading(true);
      try {
        const accessToken = await getAccessTokenSilently();

        // Prepare file metadata for all accepted files
        const filesMetadata = acceptedFiles.map((file) => ({
          filename: file.name,
          filetype: file.type || 'application/octet-stream',
        }));

        // Request pre-signed URLs for all files
        const response: GetPresignedURLsResponse = await getPresignedURLs(
          accessToken,
          filesMetadata,
          'documents-for-case-info'
        );
        const urls: PresignedURL[] = response.urls;

        // Map filenames to their corresponding URLs
        const urlMap = new Map(
          urls.map((item: { filename: string; url: string }) => [item.filename, item.url])
        );

        const successfullyUploadedFiles: string[] = [];
        const unsuccessfullyUploadedFiles: string[] = [];
        const summaries: string[] = [];

        // Upload files concurrently using Promise.all
        const uploadPromises = acceptedFiles.map(async (file) => {
          const url = urlMap.get(file.name);
          if (!url) {
            console.error(`No pre-signed URL for file ${file.name}`);
            unsuccessfullyUploadedFiles.push(file.name);
            return;
          }
          try {
            await safeAxiosPut(url, file, {
              headers: {
                'Content-Type': file.type || 'application/octet-stream',
              },
            });

            const response = await notifyFileUpload(
              accessToken,
              file.name,
              'process_case_info_file'
            );
            const summary = response.data.summary;
            successfullyUploadedFiles.push(file.name);
            const formattedSummary = `${file.name}:\n${summary}`;
            summaries.push(formattedSummary);
          } catch (error) {
            console.error(`Error uploading file ${file.name}:`, error);
            unsuccessfullyUploadedFiles.push(file.name);
          }
        });

        await Promise.all(uploadPromises);
        const combinedSummary = summaries.join('\n\n');
        onFilesProcessed(combinedSummary);

        // Provide user feedback
        let message = '';
        if (successfullyUploadedFiles.length > 0) {
          message += `${t('successfulFileUpload')} ${successfullyUploadedFiles.join(',\n')}.\n`;
        }
        if (unsuccessfullyUploadedFiles.length > 0) {
          message += `${t('unsuccessfulFileUpload')} ${unsuccessfullyUploadedFiles.join(',\n')}.`;
        }
        alert(message || t('noFilesUploaded'));
      } catch (error) {
        console.error('Error during file upload:', error);
        alert(t('errorUploadingFiles'));
      } finally {
        setIsLLMLoading(false);
      }
    },
    [getAccessTokenSilently, t, onFilesProcessed]
  );

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    multiple: true,
    accept: {
      'application/pdf': ['.pdf'],
      'application/msword': ['.doc'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
      'application/vnd.openxmlformats-officedocument.presentationml.presentation': ['.pptx'],
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/png': ['.png'],
    },
  });

  return (
    <div style={{ position: 'relative' }}>
      <LoadingOverlay visible={isLLMLoading} zIndex={1} overlayProps={{ radius: 'sm', blur: 2 }} />
      <div
        {...getRootProps()}
        style={{ border: '2px dashed #0087F7', padding: '1px', textAlign: 'center' }}
      >
        <input {...getInputProps()} />
        <p>{t('provideContextFile')}</p>
      </div>
    </div>
  );
}
